
import { AfterViewInit, Component } from '@angular/core';
import {
  Article,
  ArticleOptions,
  ArticleString,
} from '@models/permissions/permission-type.enum';
import {
  PermissionDto,
  PermissionTotalDto,
} from '@models/permissions/permission.dto';
import { DelegateDto } from '@models/users/delegate.dto';
import { PermissionDialogComponent } from '@pages/permission-management/permission/permission.component';
import { RevokePermissionComponent } from '@pages/permission-management/revoke-permission/revoke-permission.component';
import { MessageService } from '@services/message.service';
import { PermissionService } from '@services/permission.service';
import { SubscriberService } from '@services/subscriber.service';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  catchError,
  finalize,
  forkJoin,
  of,
} from 'rxjs';

@Component({
  selector: 'ebs-permission-management',
  templateUrl: './permission-management.component.html',
  styleUrls: ['./permission-management.component.scss'],
})
export class PermissionManagementComponent implements AfterViewInit {
  public items: MenuItem[] = [
    {
      command: () => this.openPermissionManagementDialog(),
      icon: PrimeIcons.PLUS_CIRCLE,
    },
    {
      command: () => this.openPermissionRevokeDialog(),
      icon: PrimeIcons.MINUS_CIRCLE,
    },
  ];

  public permissions: PermissionDto[] = [];
  public permissionTotals: PermissionTotalDto[] = [];

  // Storico: cinque permessi per pagina, senza scorrimento interno.
  public readonly permissionPageSize = 5;
  public permissionPage = 0;

  get permissionPageCount(): number {
    return Math.max(1, Math.ceil(this.permissions.length / this.permissionPageSize));
  }

  get visiblePermissions(): PermissionDto[] {
    const first = this.permissionPage * this.permissionPageSize;
    return this.permissions.slice(first, first + this.permissionPageSize);
  }

  get permissionPageStart(): number {
    return this.permissions.length === 0 ? 0 : this.permissionPage * this.permissionPageSize + 1;
  }

  get permissionPageEnd(): number {
    return Math.min((this.permissionPage + 1) * this.permissionPageSize, this.permissions.length);
  }

  previousPermissionPage(): void {
    this.permissionPage = Math.max(0, this.permissionPage - 1);
  }

  nextPermissionPage(): void {
    this.permissionPage = Math.min(this.permissionPageCount - 1, this.permissionPage + 1);
  }

  private clampPermissionPage(): void {
    this.permissionPage = Math.min(this.permissionPage, this.permissionPageCount - 1);
  }


  public currentlyEditing: PermissionTotalDto | null = null;

  public data: any[] = [];
  public delegateMap: { [key: string]: string } = {};

  public art27Data = {};
  public art30Data = {};
  public personData = {};

  public delegates: DelegateDto[] = [];
  public artOptions = ArticleOptions;
  public artOptionStrings = ArticleString;

  // Mostra il loading al posto dell'intero componente.
  public permissionsLoading = true;

  constructor(
    public dialogService: DialogService,
    private subscriberService: SubscriberService,
    private permissionService: PermissionService,
    private messageService: MessageService,
  ) {}

  ngAfterViewInit(): void {
    this.loadAll();
  }

  createNewTotal(): void {
    this.permissionTotals.splice(0, 0, {
      article: Article.Art27,
      remaining: 0,
      total: 0,
      year: new Date().getFullYear(),
    });

    this.currentlyEditing = this.permissionTotals[0];
  }

  /**
   * Carica tutti i dati della pagina.
   * Il componente diventa visibile soltanto
   * quando tutte le richieste sono terminate.
   */
  loadAll(): void {
    this.permissionsLoading = true;

    forkJoin({
      delegates: this.subscriberService.getDelegates(),
      grouped: this.permissionService.getGroupedPermission(),
      totals: this.permissionService.getPermissionTotalList(),
      permissions: this.permissionService.getPermissionList(),
    })
      .pipe(
        catchError(() => {
          this.messageService.error(
            'Gestione permessi',
            'Errore durante il caricamento dei permessi',
          );

          return of(null);
        }),
        finalize(() => {
          this.permissionsLoading = false;
        }),
      )
      .subscribe((result) => {
        if (result === null) {
          return;
        }

        this.delegates = result.delegates;
        this.data = result.grouped;
        this.permissionTotals = result.totals;
        this.permissions = result.permissions;
        this.clampPermissionPage();

        this.updateDelegateChart();
        this.updateArticleCharts();
      });
  }

  /**
   * Aggiorna il grafico delle ore per delegato.
   */
  private updateDelegateChart(): void {
    this.delegateMap = {};

    this.delegates.forEach((elem) => {
      this.delegateMap[elem.delegateId!] =
        `${elem.user?.name} ${elem.user?.lastName}`;
    });

    this.personData = {
      labels: this.data.map(
        (elem) => this.delegateMap[elem.delegate],
      ),
      datasets: [
        {
          data: this.data.map((elem) => elem.total),
        },
      ],
    };
  }

  /**
   * Aggiorna i grafici degli articoli 27 e 30.
   */
  private updateArticleCharts(): void {
    const art27 = this.permissionTotals.find(
      (elem) => elem.article === Article.Art27,
    );

    this.art27Data = {
      labels: ['Usufruito', 'Rimanente'],
      datasets: [
        {
          data: art27
            ? [
                art27.total - art27.remaining,
                art27.remaining,
              ]
            : [0, 0],
        },
      ],
    };

    const art30 = this.permissionTotals.find(
      (elem) => elem.article === Article.Art30,
    );

    this.art30Data = {
      labels: ['Usufruito', 'Rimanente'],
      datasets: [
        {
          data: art30
            ? [
                art30.total - art30.remaining,
                art30.remaining,
              ]
            : [0, 0],
        },
      ],
    };
  }

  /**
   * Mantiene disponibili i metodi originali
   * di caricamento separato.
   */
  loadDelegates(): void {
    this.subscriberService.getDelegates().subscribe({
      next: (delegates) => {
        this.permissionService.getGroupedPermission().subscribe({
          next: (group) => {
            this.data = group;
            this.delegates = delegates;
            this.updateDelegateChart();
          },
          error: () => {
            this.messageService.error(
              'Permessi',
              'Errore durante il caricamento del riepilogo',
            );
          },
        });
      },
      error: () => {
        this.messageService.error(
          'Delegati',
          'Errore durante il caricamento dei delegati',
        );
      },
    });
  }

  loadPermissions(): void {
    this.permissionService.getPermissionList().subscribe({
      next: (result) => {
        this.permissions = result;
        this.clampPermissionPage();
      },
      error: () => {
        this.messageService.error(
          'Permessi',
          'Errore durante il caricamento dello storico',
        );
      },
    });
  }

  loadPermissionTotals(): void {
    this.permissionService.getPermissionTotalList().subscribe({
      next: (result) => {
        this.permissionTotals = result;
        this.updateArticleCharts();
      },
      error: () => {
        this.messageService.error(
          'Monte ore',
          'Errore durante il caricamento del monte ore',
        );
      },
    });
  }

  removePermission(total: PermissionDto): void {
    this.permissionService
      .removePermission([total])
      .subscribe({
        next: () => {
          this.loadAll();
          this.messageService.success(
            'Rimozione permesso',
            'Rimozione avvenuta con successo',
          );
        },
        error: () => {
          this.messageService.error(
            'Rimozione permesso',
            'Errore durante la rimozione',
          );
        },
      });
  }

  openPermissionManagementDialog(): void {
    const dialogRef: DynamicDialogRef = this.dialogService.open(
      PermissionDialogComponent,
      {
        header: 'Nuovo permesso',
        styleClass: 'permission-management-dialog',
        maximizable: false,
        draggable: false,
        closable: true,
        closeOnEscape: true,
        data: {
          delegates: this.delegates,
        },
      },
    );

    dialogRef.onClose.subscribe((result: boolean) => {
      if (result) {
        this.loadAll();
      }
    });
  }

  openPermissionRevokeDialog(): void {
    this.permissionService.getRevokablePermissions().subscribe({
      next: (result) => {
        const dialogRef: DynamicDialogRef = this.dialogService.open(
          RevokePermissionComponent,
          {
            header: 'Revoca permesso',
            styleClass: 'permission-management-dialog',
            maximizable: false,
            draggable: false,
            closable: true,
            closeOnEscape: true,
            data: {
              revokable: result,
            },
          },
        );

        dialogRef.onClose.subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.loadAll();
          }
        });
      },
      error: () => {
        this.messageService.error(
          'Revoca permesso',
          'Errore durante il caricamento dei permessi revocabili',
        );
      },
    });
  }

  removeTotal(total: PermissionTotalDto): void {
    this.permissionService
      .removePermissionTotal([total])
      .subscribe({
        next: () => {
          this.currentlyEditing = null;
          this.loadAll();

          this.messageService.success(
            'Rimozione totale permesso',
            'Rimozione avvenuta con successo',
          );
        },
        error: () => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Rimozione totale permesso',
            'Errore durante la rimozione',
          );
        },
      });
  }

  editTotal(total: PermissionTotalDto | null): void {
    if (
      total === null &&
      (this.currentlyEditing?.totalId === undefined ||
        this.currentlyEditing?.totalId === null)
    ) {
      this.permissionTotals.splice(0, 1);
    }

    this.currentlyEditing = total;
  }

  modifyTotal(): void {
    if (this.currentlyEditing !== null) {
      this.saveTotalChanges(this.currentlyEditing);
    }
  }

  saveTotalChanges(total: PermissionTotalDto): void {
    this.permissionService
      .modifyPermissionTotal([total])
      .subscribe({
        next: () => {
          this.currentlyEditing = null;
          this.loadAll();

          this.messageService.success(
            'Modifica totale permesso',
            'Modifica avvenuta con successo',
          );
        },
        error: () => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Modifica totale permesso',
            'Errore durante la modifica',
          );
        },
      });
  }
}
